class ThemeShowcase {
  static boot() {
    if (typeof Swiper === 'undefined') {
      window.setTimeout(() => ThemeShowcase.boot(), 50);
      return;
    }
    ThemeShowcase.initAll();
  }

  static initAll() {
    document.querySelectorAll('[data-theme-showcase]').forEach((root) => {
      if (root.dataset.themeShowcaseInit === 'true') return;
      ThemeShowcase._whenNear(root, () => {
        if (root.dataset.themeShowcaseInit === 'true') return;
        new ThemeShowcase(root);
      });
    });
  }

  // Lazy-init: build the carousel only when the section nears the viewport, so
  // below-the-fold Swipers don't run on initial page load (cuts JS exec / TBT).
  // In the theme editor (designMode) init immediately for live preview.
  static _whenNear(el, cb) {
    if (window.Shopify && window.Shopify.designMode) { cb(); return; }
    if (el.dataset.lazyInitObserved === 'true') return;
    el.dataset.lazyInitObserved = 'true';
    if (!('IntersectionObserver' in window)) { cb(); return; }
    const io = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { obs.unobserve(entry.target); cb(); }
      });
    }, { rootMargin: '300px 0px' });
    io.observe(el);
  }

  constructor(root) {
    this.root = root;
    this.carouselEl = root.querySelector('.js-theme-showcase-carousel');
    this.bleedEl = root.querySelector('.bls__theme-showcase__carousel-bleed');
    this.navLayer = root.querySelector('.bls__theme-showcase__nav-layer');

    if (!this.carouselEl) return;

    root.dataset.themeShowcaseInit = 'true';

    this.autoplayEnabled = root.dataset.autoplay === 'true';
    this.loop = root.dataset.loop === 'true';
    this.speed = parseInt(root.dataset.speed, 10) || 500;
    this.autoplayDelay = parseInt(root.dataset.autoplayDelay, 10) || 5000;
    this.spacingDesktop = parseInt(root.dataset.spacing, 10) || 24;
    this.spacingTablet = parseInt(root.dataset.spacingTablet, 10) || this.spacingDesktop;
    this.spacingMobile = parseInt(root.dataset.spacingMobile, 10) || this.spacingTablet;
    this.slidesPerView = ThemeShowcase.withPeek(root.dataset.itemsDesktop, root.dataset.slidesPeek, 3);
    this.slidesPerViewTablet = ThemeShowcase.withPeek(root.dataset.itemsTablet, root.dataset.slidesPeek, 2);
    const mobilePeek = root.dataset.showPeekMobile === 'true' ? root.dataset.slidesPeekMobile : 0;
    this.slidesPerViewMobile = ThemeShowcase.withPeek(root.dataset.itemsMobile, mobilePeek, 1);
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.prevBtn = root.querySelector('.js-theme-showcase-prev');
    this.nextBtn = root.querySelector('.js-theme-showcase-next');
    this.itemsDesktop = parseInt(root.dataset.itemsDesktop, 10) || 3;
    this.itemsTablet = parseInt(root.dataset.itemsTablet, 10) || 2;
    this.itemsMobile = parseInt(root.dataset.itemsMobile, 10) || 1;

    this.onResize = () => this.updateNavLayout();

    this.destroySwiper();
    this.initSwiper();
    this.updateNavLayout();
    window.addEventListener('resize', this.onResize);
  }

  destroySwiper() {
    window.removeEventListener('resize', this.onResize);

    if (!this.swiper) return;

    try {
      this.swiper.destroy(true, true);
    } catch (error) {}

    this.swiper = null;
  }

  initSwiper() {
    const autoplay =
      this.autoplayEnabled && !this.reducedMotion
        ? {
            delay: this.autoplayDelay,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }
        : false;

    this.swiper = new Swiper(this.carouselEl, {
      slidesPerView: this.slidesPerViewMobile,
      spaceBetween: this.spacingMobile,
      slidesOffsetAfter: this.spacingMobile, // ← gap sau slide cuối
      speed: this.speed,
      loop: this.loop,
      autoplay,
      grabCursor: true,
      navigation: {
        nextEl: this.nextBtn,
        prevEl: this.prevBtn,
      },
      breakpoints: {
        576: {
          slidesPerView: this.slidesPerViewTablet,
          spaceBetween: this.spacingTablet,
          slidesOffsetAfter: this.spacingTablet,
        },
        1200: {
          slidesPerView: this.slidesPerView,
          spaceBetween: this.spacingDesktop,
          slidesOffsetAfter: this.spacingDesktop,
        },
      },
    });

    this.swiper.on('init', () => {
      window.requestAnimationFrame(() => this.updateNavLayout());
    });
    this.swiper.on('resize', () => this.updateNavLayout());

    window.requestAnimationFrame(() => this.updateNavLayout());
  }

  updateNavLayout() {
    if (!this.navLayer || !this.bleedEl) return;

    const media = this.carouselEl.querySelector('.swiper-slide .bls__theme-showcase__media');

    if (!media) return;

    this.navLayer.style.height = `${media.offsetHeight}px`;

    if (!this.nextBtn) return;

    const swiperWidth = this.carouselEl.offsetWidth;
    const slidesPerView = this.getCurrentSlidesPerView();
    const anchorItems = this.getNavAnchorItems();

    if (swiperWidth <= 0 || slidesPerView <= 0 || anchorItems <= 0) return;

    const spacing = this.getCurrentSpacing();
    const slideWidth = (swiperWidth - spacing * (slidesPerView - 1)) / slidesPerView;
    const nextLeft =
      slideWidth * anchorItems + spacing * Math.max(anchorItems - 1, 0) - this.nextBtn.offsetWidth - 16;

    this.nextBtn.style.left = `${Math.max(nextLeft, 16)}px`;
  }

  getCurrentSpacing() {
    const width = window.innerWidth;

    if (width >= 1200) return this.spacingDesktop;
    if (width >= 576) return this.spacingTablet;
    return this.spacingMobile;
  }

  getNavAnchorItems() {
    const width = window.innerWidth;

    if (width >= 1200) return this.itemsDesktop;
    if (width >= 576) return this.itemsTablet;
    return this.itemsMobile;
  }

  getCurrentSlidesPerView() {
    if (!this.swiper) return this.slidesPerView;

    const width = window.innerWidth;

    if (width >= 1200) return this.slidesPerView;
    if (width >= 576) return this.slidesPerViewTablet;
    return this.slidesPerViewMobile;
  }

  static withPeek(items, peekPercent, fallbackItems) {
    const count = parseFloat(items);
    const peek = parseFloat(peekPercent) / 100;
    const safeCount = Number.isFinite(count) ? count : fallbackItems;
    const safePeek = Number.isFinite(peek) ? peek : 0.25;

    return safeCount + safePeek;
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => ThemeShowcase.boot());
} else {
  ThemeShowcase.boot();
}

document.addEventListener('shopify:section:load', (event) => {
  const root = event.target.querySelector('[data-theme-showcase]');
  if (!root) return;

  root.dataset.themeShowcaseInit = '';

  if (typeof Swiper === 'undefined') {
    ThemeShowcase.boot();
    return;
  }

  new ThemeShowcase(root);
});
